import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAllData } from '../lib/api';
import { SongCard } from '../components/SongCard';
import { getArtistNames } from '../utils/artistUtils';
import { usePlayer } from '../lib/PlayerContext';

export function Home() {
  const { data, isLoading, error } = useQuery(['appData'], fetchAllData);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12 space-y-16">
        <section>
          <h2 className="font-outfit text-3xl font-semibold mb-8 flex items-center gap-3 text-pearl-white relative after:absolute after:bottom-0 after:left-0 after:w-24 after:h-px after:bg-gradient-to-r after:from-naia-pink after:to-transparent">
            <span>New to Naia</span>
            <span className="text-naia-pink text-lg">★</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="aspect-square bg-white/10 rounded-xl animate-pulse" />
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-outfit text-3xl font-semibold mb-8 flex items-center gap-3 text-pearl-white relative after:absolute after:bottom-0 after:left-0 after:w-24 after:h-px after:bg-gradient-to-r after:from-naia-pink after:to-transparent">
            <span>New Releases</span>
            <span className="text-naia-pink text-lg">✦</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="aspect-square bg-white/10 rounded-xl animate-pulse" />
            ))}
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <h2 className="font-outfit text-2xl text-pearl-white mb-4">Error loading content</h2>
        <p className="text-slate-grey mb-6">
          {error instanceof Error ? error.message : 'An unexpected error occurred'}
        </p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-naia-pink text-pearl-white rounded-lg hover:bg-soft-violet transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!data || !data.songs) {
    return (
      <div className="p-8 text-center">
        <h2 className="font-outfit text-2xl text-pearl-white mb-4">No content found</h2>
        <p className="text-slate-grey">Check back later for new songs!</p>
      </div>
    );
  }

  const newToNaia = data.songs.filter(song => 
    song.categories.includes('New to Naia')
  );

  const newReleases = data.songs.filter(song => 
    song.categories.includes('New Releases')
  );

  const getArtistName = (artistId: string) => {
    return getArtistNames(artistId, data.artists);
  };

  const getVoicebankName = (synthId: string) => {
    return data.voicebanks.find(v => v.id === synthId)?.name || 'Unknown Voicebank';
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 space-y-16">
      <section>
        <h2 className="font-outfit text-3xl font-semibold mb-8 flex items-center gap-3 text-pearl-white relative after:absolute after:bottom-0 after:left-0 after:w-24 after:h-px after:bg-gradient-to-r after:from-naia-pink after:to-transparent">
          <span>New to Naia</span>
          <span className="text-naia-pink text-lg">★</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {newToNaia.map(song => (
            <SongCard
              key={song.id}
              song={song}
              artist={getArtistName(song.artistId)}
              voicebank={getVoicebankName(song.synthId)}
            />
          ))}
          {newToNaia.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-grey">
              No songs in this category yet
            </div>
          )}
        </div>
      </section>

      <section>
        <h2 className="font-outfit text-3xl font-semibold mb-8 flex items-center gap-3 text-pearl-white relative after:absolute after:bottom-0 after:left-0 after:w-24 after:h-px after:bg-gradient-to-r after:from-naia-pink after:to-transparent">
          <span>New Releases</span>
          <span className="text-naia-pink text-lg">✦</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {newReleases.map(song => (
            <SongCard
              key={song.id}
              song={song}
              artist={getArtistName(song.artistId)}
              voicebank={getVoicebankName(song.synthId)}
            />
          ))}
          {newReleases.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-grey">
              No songs in this category yet
            </div>
          )}
        </div>
      </section>
    </div>
  );
} 